// ─────────────────────────────────────────────────────────────────────────────
// file: client/src/stores/index.js
// ─────────────────────────────────────────────────────────────────────────────
/**
 * @fileoverview PHARMAKON — Zustand stores (vente + produit + fournisseur)
 *
 * Three stores, one file — all imported as named exports.
 *
 *  useVenteStore      — vente list, POS basket, CHIFA session, submit flows
 *  useProduitStore    — produit catalogue, alert counts, CHIFA import
 *  useFournisseurStore — fournisseur CRUD + dropdown list
 *
 * Design principles
 * ──────────────────────────────────────────────────────────────────────────────
 *  • No persistence — all state is session-only (security: CHIFA session data
 *    must never survive a page reload)
 *  • Every async action: loading → result | error → loading=false
 *  • Optimistic updates where safe (list mutations)
 *  • POS basket: addToBasket is idempotent on lot ID (increments qty)
 */

import { create } from "zustand";
import {
  venteApi,
  produitApi,
  fournisseurApi,
  notificationApi,
  clientApi,
  creditApi,
} from "../services/index.js";
import { fetchAllPages } from "../lib/fetchAllPages.js";
import { createRequestSequence, isAbortError } from "../lib/request-sequence.js";
import { partsChifaCentimes, versDinars } from "@pharmakon/shared/pricing";

// ─────────────────────────────────────────────────────────────────────────────
// useVenteStore
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages:
 *  • Vente history list (paginated, filterable)
 *  • POS basket for Vente Libre and CHIFA
 *  • CHIFA session header (ephemeral, never persisted)
 *  • Submit flows: submitLibre / submitChifa (create → validate in one call)
 */
export const useVenteStore = create((set, get) => ({
  // ── List state ─────────────────────────────────────────────────────────────
  ventes: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  selected: null,

  filters: {
    statut: "",
    type_vente: "",
    caisse_id: "",
    date_debut: "",
    date_fin: "",
    search: "",
    sort_by: "date_vente",
    sort_order: "desc",
  },

  // ── POS Basket ─────────────────────────────────────────────────────────────
  basket: [],
  basketType: "LIBRE", // 'LIBRE' | 'CHIFA' | 'CREDIT'
  chifaSession: null, // VenteChifa session header — ephemeral

  // ── Feature 2 + 3 (2026-05) — Client + outstanding-debt banner ────────────
  /** Selected client for the current sale. Required for CREDIT. */
  currentClient: null,
  /** { hasDebt, total, unpaid: Vente[] } — informational, never blocks. */
  clientDebtWarning: null,

  // ── Setters ────────────────────────────────────────────────────────────────
  setFilters: (patch) =>
    set((s) => ({ filters: { ...s.filters, ...patch }, page: 1 })),
  setPage: (p) => set({ page: p }),
  setSelected: (v) => set({ selected: v }),
  clearError: () => set({ error: null }),

  // ── List fetch ─────────────────────────────────────────────────────────────
  fetch: async () => {
    const { page, limit, filters } = get();
    set({ loading: true, error: null });
    try {
      const r = await venteApi.list({ page, limit, ...filters });
      set({
        ventes: r.data ?? r.data?.data ?? [],
        total: r.total ?? r.data?.total ?? 0,
        loading: false,
      });
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  fetchById: async (id) => {
    set({ loading: true, error: null });
    try {
      const r = await venteApi.getById(id);
      set({ selected: r.data ?? r.data?.data, loading: false });
      return r.data ?? r.data?.data;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  // ── Mutations ──────────────────────────────────────────────────────────────

  /** Cancel a VALIDEE vente and restore stock. Refreshes selected + list. */
  cancel: async (id, motif) => {
    set({ loading: true, error: null });
    try {
      const r = await venteApi.cancel(id, motif);
      const updated = r.data;
      set((s) => ({
        ventes: s.ventes.map((v) => (v.id === id ? updated : v)),
        selected: s.selected?.id === id ? updated : s.selected,
        loading: false,
      }));
      return updated;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  /** Delete an EN_COURS vente. Removes from list. */
  remove: async (id) => {
    try {
      await venteApi.delete(id);
      set((s) => ({
        ventes: s.ventes.filter((v) => v.id !== id),
        total: s.total - 1,
      }));
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  /** Update detail line on an EN_COURS vente */
  updateDetail: async (venteId, detailId, body) => {
    try {
      const r = await venteApi.updateDetail(venteId, detailId, body);
      const updated = r.data;
      set((s) => ({
        selected: s.selected?.id === venteId ? updated : s.selected,
      }));
      return updated;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  /** Remove detail line from an EN_COURS vente */
  removeDetail: async (venteId, detailId) => {
    try {
      await venteApi.removeDetail(venteId, detailId);
      // Refresh the selected vente to get updated totals
      const r = await venteApi.getById(venteId);
      set({ selected: r.data });
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  // ── POS Basket ─────────────────────────────────────────────────────────────

  /**
   * Add a product+lot to the basket.
   * If the lot is already present, increments quantity by qty.
   * max_qty enforced from stock_lot.quantite_disponible.
   */
  addToBasket: (produit, lot, qty = 1) =>
    set((s) => {
      const existing = s.basket.find((i) => i.stock_lot_id === lot.id);
      if (existing) {
        return {
          basket: s.basket.map((i) =>
            i.stock_lot_id === lot.id
              ? { ...i, quantite: Math.min(i.quantite + qty, i.max_qty) }
              : i,
          ),
        };
      }
      return {
        basket: [
          ...s.basket,
          {
            produit_id: produit.id,
            nom: produit.nom_commercial,
            dci: produit.dci?.nom ?? "",
            code_barre: produit.code_barre ?? "",
            stock_lot_id: lot.id,
            numero_lot: lot.numero_lot ?? "",
            date_peremption: lot.date_peremption,
            quantite: qty,
            max_qty: lot.quantite_disponible,
            prix_unitaire_ppa: parseFloat(
              lot.prix_vente_ppa ?? produit.ppa ?? 0,
            ),
            // CHIFA fields — filled in by VenteChifa component
            num_enr_cnas: produit.num_enr_cnas ?? null,
            tarif_remb: produit.tarif_ref ?? null,
            is_remboursable: produit.is_remboursable ?? false,
            is_local: produit.is_local ?? false,
          },
        ],
      };
    }),

  /** Update quantity for a lot line. Removes line if qty ≤ 0. */
  updateBasketQty: (stock_lot_id, qty) =>
    set((s) => ({
      basket:
        qty <= 0
          ? s.basket.filter((i) => i.stock_lot_id !== stock_lot_id)
          : s.basket.map((i) =>
            i.stock_lot_id === stock_lot_id
              ? { ...i, quantite: Math.min(qty, i.max_qty) }
              : i,
          ),
    })),

  removeFromBasket: (stock_lot_id) =>
    set((s) => ({
      basket: s.basket.filter((i) => i.stock_lot_id !== stock_lot_id),
    })),

  clearBasket: () =>
    set({
      basket: [],
      basketType: "LIBRE",
      currentClient: null,
      clientDebtWarning: null,
    }),
  setBasketType: (t) => set({ basketType: t }),

  // ── CHIFA session (ephemeral) ───────────────────────────────────────────────

  setChifaSession: (data) => set({ chifaSession: data }),
  clearChifaSession: () => set({ chifaSession: null }),

  // ── Feature 2 + 3 (2026-05) — client + debt warning ────────────────────────

  /**
   * Attach a client to the current sale. If the client has unpaid CREDIT
   * ventes, populate `clientDebtWarning` with the total + list so the SPA
   * can render a banner above the basket. Lookup failures degrade silently
   * (the warning is informational only).
   */
  setCurrentClient: async (client) => {
    if (!client) {
      set({ currentClient: null, clientDebtWarning: null });
      return;
    }
    set({ currentClient: client, clientDebtWarning: null });
    try {
      const r = await clientApi.getById(client.id);
      const ventes = r?.data?.ventes ?? [];
      const unpaid = ventes.filter(
        (v) => v.type_vente === "CREDIT" && !v.paid_at,
      );
      if (unpaid.length === 0) return;
      const total = unpaid.reduce(
        (s, v) => s + Number(v.montant_du ?? 0),
        0,
      );
      set({ clientDebtWarning: { hasDebt: true, total, unpaid } });
    } catch {
      /* silent — warning is informational only */
    }
  },

  /**
   * Submit a Crédit (deferred payment).
   * Requires `currentClient`. Server runs a Serializable transaction
   * decrementing stock + (optionally) recording the CardCustody.
   *
   * `details` may be passed in (for pages that keep a local basket); when
   * omitted, falls back to the store basket.
   */
  submitCredit: async ({
    details: detailsArg,
    carte_chifa_retenue = false,
    carte_numero = null,
    caisse_id = null,
    notes = null,
  } = {}) => {
    const { basket, currentClient } = get();
    if (!currentClient)
      throw new Error("Aucun client sélectionné pour le crédit");

    const src = detailsArg && detailsArg.length > 0 ? detailsArg : basket;
    if (!src.length) throw new Error("Le panier est vide");

    set({ loading: true, error: null });
    try {
      const body = {
        client_id: currentClient.id,
        ...(caisse_id ? { caisse_id } : {}),
        carte_chifa_retenue: !!carte_chifa_retenue,
        ...(carte_chifa_retenue && carte_numero ? { carte_numero } : {}),
        details: src.map((i) => ({
          produit_id: i.produit_id,
          stock_lot_id: i.stock_lot_id,
          quantite: i.quantite,
          prix_unitaire: i.prix_unitaire ?? i.prix_unitaire_ppa ?? i.ppa,
          remise: i.remise ?? 0,
        })),
        ...(notes ? { notes } : {}),
      };
      const r = await creditApi.create(body);
      const vente = r?.data ?? r;
      set((s) => ({
        ventes: [vente, ...s.ventes],
        basket: [],
        basketType: "LIBRE",
        currentClient: null,
        clientDebtWarning: null,
        loading: false,
      }));
      return vente;
    } catch (e) {
      set({ error: e?.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  // ── POS Submit ─────────────────────────────────────────────────────────────

  /**
   * Submit a Vente Libre: create → validate (FEFO deduction).
   * Returns the validated vente or throws on error.
   *
   * @param {{ remise_globale_pct?: number, caisse_id?: string }} opts
   */
  submitLibre: async ({ remise_globale_pct = 0, caisse_id } = {}) => {
    const { basket } = get();
    if (!basket.length) throw new Error("Le panier est vide");

    set({ loading: true, error: null });
    try {
      // 1. Create EN_COURS
      const created = await venteApi.createLibre({
        caisse_id,
        remise_globale_pct,
        details: basket.map((i) => ({
          produit_id: i.produit_id,
          stock_lot_id: i.stock_lot_id,
          quantite: i.quantite,
          prix_unitaire_ppa: i.prix_unitaire_ppa,
        })),
      });
      const venteId = created.data.id;

      // 2. Validate → stock deducted
      const validated = await venteApi.validate(venteId);
      const vente = validated.data;

      // 3. Clear basket
      set({ loading: false, basket: [], basketType: "LIBRE" });
      return vente;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  /**
   * Submit a Vente CHIFA: create → validate.
   * Requires a valid chifaSession in state.
   *
   * @param {{ caisse_id?: string }} opts
   */
  submitChifa: async ({ caisse_id } = {}) => {
    const { basket, chifaSession } = get();
    if (!basket.length) throw new Error("Le panier est vide");
    if (!chifaSession) throw new Error("Session CHIFA manquante");

    // Per-line CHIFA amounts — the shared split (integer centimes, the TR capped at the PPA, the
    // same function as the server: refonte WP-42, CHIFA-AUD-05).
    let totalAS = 0;
    let totalPS = 0;

    const details = basket.map((i) => {
      let montant_assure = null;
      let montant_officine = null;
      const c = partsChifaCentimes({
        ppa: i.prix_unitaire_ppa,
        tr: i.is_remboursable ? i.tarif_remb : 0,
        taux_pec: chifaSession.taux_pec,
        quantite: i.quantite,
      });
      if (i.is_remboursable && i.tarif_remb > 0) {
        montant_officine = versDinars(c.officine);
        montant_assure = versDinars(c.assure);
        totalPS += c.officine;
      }
      totalAS += c.assure;
      return {
        produit_id: i.produit_id,
        stock_lot_id: i.stock_lot_id,
        quantite: i.quantite,
        prix_unitaire_ppa: i.prix_unitaire_ppa,
        num_enr_cnas: i.num_enr_cnas ?? null,
        tarif_remb: i.tarif_remb ?? null,
        montant_assure,
        montant_officine,
        is_remboursable: i.is_remboursable,
      };
    });

    set({ loading: true, error: null });
    try {
      const created = await venteApi.createChifa({
        caisse_id,
        details,
        // Total à payer (PS + majoration + FAE) is computed by the server (DB-25).
        chifa: {
          ...chifaSession,
          montant_assure_total: versDinars(totalAS),
          montant_officine_total: versDinars(totalPS),
          montant_fact: versDinars(totalAS + totalPS),
        },
      });
      const venteId = created.data.id;
      const validated = await venteApi.validate(venteId);
      const vente = validated.data;

      set({
        loading: false,
        basket: [],
        basketType: "LIBRE",
        chifaSession: null,
      });
      return vente;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  /**
   * Direct vente creation — for pages that build their own payload
   * (CaisseLibre, VenteChifa). Normalises field aliases and dispatches
   * to the correct endpoint based on type_vente.
   *
   * @param {{ type_vente?: string, details: object[], [key: string]: any }} payload
   */
  createVente: async (payload) => {
    const {
      type_vente = "LIBRE",
      details = [],
      // Phase 1 — psychotrope dispensing inputs, keyed by produit_id +
      // stock_lot_id (detail IDs aren't known until after create).
      ordonnanciers = null,
      // Phase 5 — optional loyalty settlement { carte_fidelite_id,
      // points_reedimes }. Vente-level, so no detail mapping is needed.
      fidelite = null,
      // Phase 6 — optional POS payment method + TPE/cheque reference.
      methode_paiement = null,
      reference_paiement = null,
      aide_conseil = null,
      ...rest
    } = payload;

    // Normalise detail line aliases (prix_vente_ppa → prix_unitaire_ppa)
    const normDetails = details.map(({ prix_vente_ppa, ...d }) => ({
      ...d,
      prix_unitaire_ppa: d.prix_unitaire_ppa ?? prix_vente_ppa,
    }));

    // Feature 2 — attach the selected client (if any) so the server can
    // surface a debt warning + persist the link for client history.
    const { currentClient } = get();
    const withClient =
      currentClient && rest.client_id == null
        ? { ...rest, client_id: currentClient.id }
        : rest;

    const apiFn = type_vente === "CHIFA" ? venteApi.createChifa : venteApi.createLibre;
    const created = await apiFn({ ...withClient, details: normDetails });
    const vente = created.data;
    const venteId = vente.id;

    // Phase 1 — resolve each dispensing input to its created detail line id and
    // forward to validate so the ordonnancier register entry is materialised.
    let validateBody;
    if (Array.isArray(ordonnanciers) && ordonnanciers.length) {
      const createdDetails = vente.details ?? [];
      const mapped = ordonnanciers
        .map((o) => {
          const match =
            createdDetails.find(
              (d) =>
                d.produit_id === o.produit_id &&
                d.stock_lot_id === o.stock_lot_id,
            ) ?? createdDetails.find((d) => d.produit_id === o.produit_id);
          if (!match) return null;
          return {
            detail_vente_id: match.id,
            medecin_id: o.medecin_id ?? null,
            no_ordonnance: o.no_ordonnance,
            date_ordonnance: o.date_ordonnance,
            nom_patient: o.nom_patient,
            nom_porteur: o.nom_porteur ?? null,
            carte_porteur: o.carte_porteur ?? null,
          };
        })
        .filter(Boolean);
      if (mapped.length) validateBody = { ordonnanciers: mapped };
    }

    // Phase 5 — attach loyalty settlement (card + points to redeem). Merged
    // into the same validate body; the server earns/redeems post-validate.
    if (fidelite && fidelite.carte_fidelite_id) {
      validateBody = {
        ...(validateBody ?? {}),
        fidelite: {
          carte_fidelite_id: fidelite.carte_fidelite_id,
          points_reedimes: Math.max(0, Math.floor(Number(fidelite.points_reedimes) || 0)),
        },
      };
    }

    // Phase 6 — attach the POS payment method + optional reference.
    if (methode_paiement) {
      validateBody = {
        ...(validateBody ?? {}),
        methode_paiement,
        ...(reference_paiement ? { reference_paiement } : {}),
      };
    }

    if (aide_conseil) {
      validateBody = {
        ...(validateBody ?? {}),
        aide_conseil,
      };
    }

    try {
      const validated = await venteApi.validate(venteId, validateBody);
      return validated.data;
    } catch (e) {
      // Aide au conseil — on a refusal the draft exists: hand its id and validate body back so a
      // pharmacist-authorised retry validates THIS draft (validerVenteExistante) instead of a new one.
      if (e && typeof e === "object") {
        e.venteId = venteId;
        e.validateBody = validateBody ?? null;
      }
      throw e;
    }
  },

  /** Validate an existing draft vente (the retry after an interaction gate refusal). */
  validerVenteExistante: async (venteId, body) => {
    const validated = await venteApi.validate(venteId, body);
    return validated.data;
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// useProduitStore
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Manages:
 *  • Product catalogue (paginated, filterable)
 *  • Alert counts for sidebar badge (fast)
 *  • Alert rows for StockDashboard detail panel (full)
 *  • CHIFA import flow
 *  • Autocomplete search (returns array directly, not stored globally)
 */
/** The /produits list request sequence (one list on screen at a time). */
const produitListeSequence = createRequestSequence();

export const useProduitStore = create((set, get) => ({
  // ── List ───────────────────────────────────────────────────────────────────
  produits: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  selected: null,

  filters: {
    search: "",
    type_produit: "",
    is_remboursable: "",
    sort_by: "nom_commercial",
    sort_order: "asc",
  },

  // ── Alerts ─────────────────────────────────────────────────────────────────
  /** Counts for sidebar badge — lightweight */
  alertCounts: {
    lotsPerimes: 0,
    lotsProchesPeremption: 0,
    produitsStockBas: 0,
    produitsEnRupture: 0,
  },

  /** Full alert rows for StockDashboard detail tables */
  alertRows: {
    expired: [],
    nearExpiry: [],
    lowStock: [],
    outOfStock: [],
  },

  alertsLoading: false,

  // ── Setters ────────────────────────────────────────────────────────────────
  setFilters: (patch) =>
    set((s) => ({ filters: { ...s.filters, ...patch }, page: 1 })),
  setPage: (p) => set({ page: p }),
  clearError: () => set({ error: null }),

  // ── Fetch list ─────────────────────────────────────────────────────────────
  // Refonte WP-37 / XWP-12 (AUD-PROD-09, AUD-SRCH-06) — one request sequence: a new fetch aborts
  // the one in flight and a late answer for an older query is dropped, so the rows always belong
  // to the filters on screen (the list never shows « nova » rows under a « nova mico » chip).
  fetch: async () => {
    const { page, limit, filters } = get();
    const ticket = produitListeSequence.begin();
    set({ loading: true, error: null });
    try {
      const r = await produitApi.list({ page, limit, ...filters }, { signal: ticket.signal });
      if (!produitListeSequence.isCurrent(ticket)) return;
      set({
        produits: r.data ?? [],
        total: r.total ?? 0,
        loading: false,
      });
    } catch (e) {
      if (!produitListeSequence.isCurrent(ticket) || isAbortError(e)) return;
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  fetchById: async (id) => {
    set({ loading: true });
    try {
      const r = await produitApi.getById(id);
      set({ selected: r.data, loading: false });
      return r.data;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  // ── CRUD ───────────────────────────────────────────────────────────────────
  create: async (body) => {
    try {
      const r = await produitApi.create(body);
      const p = r.data;
      set((s) => ({ produits: [p, ...s.produits], total: s.total + 1 }));
      return p;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  update: async (id, body) => {
    try {
      const r = await produitApi.update(id, body);
      const p = r.data;
      set((s) => ({
        produits: s.produits.map((x) => (x.id === id ? p : x)),
        selected: p,
      }));
      return p;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  remove: async (id) => {
    try {
      await produitApi.delete(id);
      set((s) => ({
        produits: s.produits.filter((x) => x.id !== id),
        total: s.total - 1,
      }));
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
      throw e;
    }
  },

  // ── Alerts ─────────────────────────────────────────────────────────────────
  // NOTE: server /produits/alerts returns { expired[], nearExpiry[], lowStock[], outOfStock[] }
  // Counts are derived from array lengths — no "counts" wrapper in response.

  /** Fast counts only — for sidebar badge. */
  fetchAlertCounts: async () => {
    try {
      const r = await produitApi.alertCounts();
      set({ alertCounts: r.data ?? {} });
    } catch {
      /* silent — badge just stays at 0 */
    }
  },

  /** Full alert rows for StockDashboard. */
  fetchAlerts: async () => {
    set({ alertsLoading: true });
    try {
      const [rAlerts, rCounts] = await Promise.all([
        produitApi.alerts(),
        produitApi.alertCounts(),
      ]);

      const d = rAlerts.data ?? {};
      const expired = d.expired ?? [];
      const nearExpiry = d.nearExpiry ?? [];
      const lowStock = d.lowStock ?? [];
      const outOfStock = d.outOfStock ?? [];

      set({
        alertCounts: rCounts.data ?? {
          lotsPerimes: 0,
          lotsProchesPeremption: 0,
          produitsStockBas: 0,
          produitsEnRupture: 0,
        },
        alertRows: { expired, nearExpiry, lowStock, outOfStock },
        alertsLoading: false,
      });
    } catch {
      set({ alertsLoading: false });
    }
  },

  // Search / autocomplete: the one ProductPicker (hooks/useProductSearch.js, XWP-12 / WP-37) — the
  // store helper that turned a failed search into « no result » is gone.

  // ── CHIFA import ───────────────────────────────────────────────────────────

  /** Import product from CNAS DB by num_enr. Adds/replaces in list. */
  importChifa: async (num_enr) => {
    set({ loading: true, error: null });
    try {
      const r = await produitApi.importChifa(num_enr);
      const p = r.data;
      set((s) => {
        const idx = s.produits.findIndex((x) => x.id === p.id);
        return {
          produits:
            idx >= 0
              ? s.produits.map((x) => (x.id === p.id ? p : x))
              : [p, ...s.produits],
          loading: false,
        };
      });
      return p;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// useFournisseurStore
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Batch-1 élément 16 — insère ou remplace un fournisseur dans le cache
 * `dropdown`, en conservant l'ordre alphabétique par `nom` que `fetchDropdown`
 * demande au serveur (`sort_by: "nom", sort_order: "asc"`).
 *
 * Fonction PURE et exportée : c'est elle que le test épingle, sans avoir à
 * monter le store ni le réseau.
 *
 * Cas particulier volontaire — un `dropdown` VIDE reste vide. Une liste vide
 * ne veut pas dire « aucun fournisseur », elle veut dire « pas encore
 * chargée » : y déposer une seule entrée ferait croire aux sélecteurs (qui ne
 * rechargent que « si la liste est vide ») que le catalogue tient en un nom.
 *
 * @param {Array<{id: string, nom?: string}>} dropdown
 * @param {{id: string, nom?: string}} f
 * @returns {Array} le cache trié
 */
export function _dropdownUpsert(dropdown, f) {
  const liste = Array.isArray(dropdown) ? dropdown : [];
  if (liste.length === 0 || !f?.id) return liste;
  const sans = liste.filter((x) => x.id !== f.id);
  return [...sans, f].sort((a, b) =>
    String(a?.nom ?? "").localeCompare(String(b?.nom ?? ""), "fr", {
      sensitivity: "base",
    }),
  );
}

/**
 * Manages:
 *  • Fournisseur list (paginated, searchable)
 *  • Flat dropdown list for NouvelAchat <select>
 *  • Full CRUD: create, update, remove
 */
export const useFournisseurStore = create((set, get) => ({
  // ── List ───────────────────────────────────────────────────────────────────
  fournisseurs: [],
  total: 0,
  page: 1,
  limit: 20,
  loading: false,
  error: null,
  selected: null,

  /** Flat list for dropdowns — aggregated across pages (see fetchDropdown) */
  dropdown: [],
  /** Last dropdown fetch failure (R3-H28 — no longer silently swallowed). */
  dropdownError: null,

  filters: {
    search: "",
    sort_by: "nom",
    sort_order: "asc",
  },

  // ── Setters ────────────────────────────────────────────────────────────────
  setFilters: (patch) =>
    set((s) => ({ filters: { ...s.filters, ...patch }, page: 1 })),
  setPage: (p) => set({ page: p }),
  setSelected: (f) => set({ selected: f }),
  clearError: () => set({ error: null }),

  // ── Fetch ──────────────────────────────────────────────────────────────────
  fetch: async () => {
    const { page, limit, filters } = get();
    set({ loading: true, error: null });
    try {
      const r = await fournisseurApi.list({ page, limit, ...filters });
      set({
        fournisseurs: r.data ?? [],
        total: r.total ?? 0,
        loading: false,
      });
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  /**
   * Load ALL fournisseurs into the dropdown, ordered by nom.
   *
   * R3-H28 — the old single call requested limit=500, above the server's
   * LIST_LIMIT_MAX cap (422 "Limite max : 200"), so the supplier pickers on
   * /achats, /commercial/catalogue and /commercial/achat-urgent were
   * deterministically empty. fetchAllPages walks the list endpoint within
   * the cap; a failure is recorded on dropdownError instead of being
   * swallowed, so pickers can explain an empty list.
   */
  fetchDropdown: async () => {
    try {
      const rows = await fetchAllPages((pageParams) =>
        fournisseurApi.list({
          ...pageParams,
          sort_by: "nom",
          sort_order: "asc",
        }),
      );
      set({ dropdown: rows, dropdownError: null });
    } catch (e) {
      set({
        dropdownError:
          e?.response?.data?.message ??
          e?.message ??
          "Chargement des fournisseurs impossible",
      });
    }
  },

  fetchById: async (id) => {
    set({ loading: true });
    try {
      const r = await fournisseurApi.getById(id);
      set({ selected: r.data, loading: false });
      return r.data;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  // ── CRUD ───────────────────────────────────────────────────────────────────
  //
  // Batch-1 élément 16 — « un fournisseur créé doit apparaître dans l'Appel à
  // l'offre ». RACINE DU DÉFAUT, mesurée dans ce fichier : `dropdown` est un
  // cache d'AGRÉGAT (fetchDropdown parcourt toutes les pages une fois), et les
  // trois écrivains ci-dessous ne mettaient à jour que `fournisseurs`, la page
  // courante. Les sélecteurs qui lisent `dropdown` — l'appel d'offres
  // (`RfqBroadcast.jsx`), l'achat urgent, le catalogue — ne se rechargent que
  // « si la liste est vide » : une fois le cache peuplé, un fournisseur créé
  // dans la même session n'y entrait JAMAIS, jusqu'au rechargement de la page.
  //
  // Le plan (§4.3) supposait une autre cause — « la liste est celle des
  // fournisseurs connectés au portail ». Ce n'est plus vrai du code : la page
  // lit `useFournisseurStore.dropdown`, c'est-à-dire `GET /fournisseurs`, sans
  // aucun filtre de lien portail. La cause réelle est ce cache non tenu à jour,
  // et c'est ce qui est corrigé ici — au STORE, donc pour les quatre
  // sélecteurs à la fois, plutôt que page par page.
  //
  // `dropdown` reste trié par `nom` (l'ordre que `fetchDropdown` demande au
  // serveur) : un sélecteur qui range ses fournisseurs par ordre alphabétique
  // ne doit pas voir le dernier créé se poser en tête.
  create: async (body) => {
    set({ loading: true, error: null });
    try {
      const r = await fournisseurApi.create(body);
      const f = r.data;
      set((s) => ({
        fournisseurs: [f, ...s.fournisseurs],
        dropdown: _dropdownUpsert(s.dropdown, f),
        total: s.total + 1,
        loading: false,
      }));
      return f;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  update: async (id, body) => {
    set({ loading: true, error: null });
    try {
      const r = await fournisseurApi.update(id, body);
      const f = r.data;
      set((s) => ({
        fournisseurs: s.fournisseurs.map((x) => (x.id === id ? f : x)),
        // Un fournisseur renommé doit se retrouver sous son NOUVEAU nom dans
        // les sélecteurs, et à la bonne place alphabétique.
        dropdown: _dropdownUpsert(s.dropdown, f),
        selected: s.selected?.id === id ? f : s.selected,
        loading: false,
      }));
      return f;
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },

  remove: async (id) => {
    set({ loading: true, error: null });
    try {
      await fournisseurApi.delete(id);
      set((s) => ({
        fournisseurs: s.fournisseurs.filter((x) => x.id !== id),
        // Symétrie : un fournisseur supprimé ne doit plus être proposé — sinon
        // l'appel d'offres partirait vers un tiers qui n'existe plus.
        dropdown: s.dropdown.filter((x) => x.id !== id),
        total: s.total - 1,
        selected: s.selected?.id === id ? null : s.selected,
        loading: false,
      }));
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
      throw e;
    }
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// NOTIFICATION STORE
// Polls /notifications/count every 30s. Full list fetched on bell open.
// ─────────────────────────────────────────────────────────────────────────────

export const useNotificationStore = create((set, get) => ({
  // State
  notifications: [],
  total: 0,
  unread: 0,
  page: 1,
  loading: false,
  error: null,
  pollerRef: null,

  // ── Read ──────────────────────────────────────────────────────────────────

  /** Fetch unread count only — lightweight, used by AppShell poll */
  fetchCount: async () => {
    try {
      const r = await notificationApi.count();
      // Server returns `{success, data: {count: N}}` (interceptor strips outer
      // envelope). Defend against both shapes in case the envelope ever
      // changes — never let a parsing glitch leave the badge stuck.
      const count =
        typeof r?.data?.count === "number"
          ? r.data.count
          : typeof r?.count === "number"
            ? r.count
            : 0;
      set({ unread: count });
    } catch {
      /* silent — polling failures must not break the bell */
    }
  },

  /** Fetch full list with pagination.
   *
   * The server's `GET /notifications` envelope is
   *   `{ success, data: { data: [...], total, page, pages } }`
   * — i.e. the paged result is itself the `data` field. The axios interceptor
   * strips the outer envelope, so `r` is `{ success, data: { ... } }` and
   * `r.data` is the paged payload.
   *
   * Bug fix (2026-05-28 — restored after design migration):
   *   Previous destructure tried to read `unread` from the paged payload, but
   *   the list endpoint never returns that field. `unread` was being set to
   *   `undefined` on every `fetch()`, silently overwriting the count that the
   *   30s `fetchCount` poll had populated. Result: the bell badge would flash
   *   the correct count then disappear the next time the panel opened. We
   *   now (a) ignore `unread` from this response and (b) re-run `fetchCount`
   *   in the background so the badge is always consistent with the list.
   */
  fetch: async ({ page = 1, unread_only = false } = {}) => {
    set({ loading: true, error: null });
    try {
      const r = await notificationApi.list({ page, limit: 30, unread_only });
      const payload = r?.data ?? {};
      const list = Array.isArray(payload.data) ? payload.data : [];
      const total = Number(payload.total ?? list.length);
      set((s) => ({
        notifications: list,
        total,
        page,
        loading: false,
        // Recompute unread from the freshly-fetched list as a defensive
        // floor; the actual badge truth comes from /count below.
        unread: list.filter((n) => !n.lue).length || s.unread,
      }));
      // Refresh the badge count out-of-band — the list might be paginated
      // and only show the first 30, so an authoritative count helps the
      // badge stay correct.
      get().fetchCount?.();
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message, loading: false });
    }
  },

  // ── Write ─────────────────────────────────────────────────────────────────

  markRead: async (id) => {
    try {
      await notificationApi.markRead(id);
      set((s) => ({
        notifications: s.notifications.map((n) =>
          n.id === id ? { ...n, lue: true } : n,
        ),
        unread: Math.max(0, s.unread - 1),
      }));
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
    }
  },

  markAllRead: async () => {
    try {
      await notificationApi.markAllRead();
      set((s) => ({
        notifications: s.notifications.map((n) => ({ ...n, lue: true })),
        unread: 0,
      }));
    } catch (e) {
      set({ error: e.response?.data?.message ?? e.message });
    }
  },

  // ── Polling ───────────────────────────────────────────────────────────────

  /** Start 30s poll. Call once from AppShell on mount. */
  startPoll: () => {
    const { fetchCount, pollerRef } = get();
    if (pollerRef) return; // already running
    fetchCount();
    const ref = setInterval(fetchCount, 30_000);
    set({ pollerRef: ref });
  },

  stopPoll: () => {
    const { pollerRef } = get();
    if (pollerRef) {
      clearInterval(pollerRef);
      set({ pollerRef: null });
    }
  },
}));

// ─────────────────────────────────────────────────────────────────────────────
// AVOIR STORE — the « Journal des avoirs fournisseur » (refonte WP-30, stores/avoirStore.js)
// ─────────────────────────────────────────────────────────────────────────────

export { useAvoirStore } from "./avoirStore.js";
